<template>
  <DefaultWrapper>
    <RecentPagePdfButton v-if="enableDownloadPDFButton && department.id" class="absolute right-2 top-13" :page-id="'department-' + department.id" />
    <h1 class="p-4 md:p-8 pb-2 mb-0">
      Departments: {{ department.displayName }}
    </h1>
    <p
      v-if="department.catalogDescription && enableCatalogDescriptions"
      class="md:px-8 md:pb-8"
      data-test="curriculumDepartmentCatalogDescription"
    >
      {{ department.catalogDescription }}
    </p>
    <ul class="flex border-b px-4 md:px-8" data-test="dept-tab-list">
      <li
        v-for="tab of tabs"
        :key="tab.to"
        class="-mb-px mr-1"
      >
        <nuxt-link
          class="bg-white inline-block py-2 px-2 md:px-4 text-blue-600 font-semibold ui-font"
          :to="`/departments/${encodeURIComponent($route.params.id)}/${tab.to}`"
        >
          {{ tab.label }}
        </nuxt-link>
      </li>
    </ul>
    <div class="px-4 md:px-8 pb-8 pt-2" data-test="tab-content">
      <nuxt-child
        keep-alive
        :department="department"
        :page="page"
      />
    </div>
  </DefaultWrapper>
</template>

<script>
import { ref } from '@nuxtjs/composition-api'
import RecentPagePdfButton from '@/components/RecentPagePdfButton.vue'
import pageLayout from '@/mixins/pageLayoutMixin'
import useMeta from '@/composables/useMeta'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  DEPARTMENT_CATALOG_DESCRIPTIONS,
  DEPARTMENT_PAGE_PDF,
} from '@/constants/featureFlags'
import DefaultWrapper from '@/themes/default/DefaultWrapper'

export default {
  components: { DefaultWrapper, RecentPagePdfButton },
  setup() {
    const department = ref({})
    const page = ref({})
    const {
      enableCatalogDescriptions,
      enableDownloadPDFButton,
    } = useFeatureFlags({
      enableCatalogDescriptions: DEPARTMENT_CATALOG_DESCRIPTIONS,
      enableDownloadPDFButton: DEPARTMENT_PAGE_PDF,
    })
    const { catalogWideMetaTitle } = useMeta()
    return {
      department,
      page,
      enableCatalogDescriptions,
      enableDownloadPDFButton,
      catalogWideMetaTitle,
    } // now we can use enableCatalogDescriptions for v-if to enable or disable in template
  },

  mixins: [pageLayout],
  head() {
    return {
      title: `${this.department.displayName} Department | ${
        this.catalogWideMetaTitle
      }`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `${this.department.displayName} Department | ${
            this.catalogWideMetaTitle
          }`,
        },
      ],
    }
  },
  computed: {
    tabs() {
      return [
        { to: 'overview', label: 'Overview' },
        { to: 'courses', label: 'Courses' },
        { to: 'programs', label: 'Programs' },
      ]
    },
  },
  async created() {
    const url = `/ca/${this.$store.state.settings.school}/catalogPages/${
      this.$store.state.settings.activeCatalog
    }/department-${encodeURIComponent(this.$route.params.id)}`

    try {
      const [department, page] = await Promise.all([
        this.$axios.$get(
          `/${
            this.$store.state.settings.school
          }/departments/${encodeURIComponent(this.$route.params.id)}`
        ),
        this.$axios.$get(url).catch((e) => {
          console.warn('Missing department page')
        }),
      ])
      this.page = page
      this.department = department
      this.$store.commit('page/SET_CURRENT_PAGE_LABEL', department.displayName)
    } catch (e) {
      this.$bugsnag.notify(e)
      this.$router.redirect('/404')
    }
  },
}
</script>

<style lang="css" scoped>
.nuxt-link-active {
  @apply border-l border-t border-r rounded-t;
}
.ui-font,
.ui-font:hover,
.ui-font:focus {
  font-weight: var(--interfaceFontWeight, inherit);
  color: var(--interfaceFontColor, inherit);
}
</style>
